import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { clientMutation, clientQuery } from '../../../../../../../core/data/GraphQLApi';
import { endpoints } from '../../../../../../../core/defaultValues';
import { ExceptionManager } from '../../../../../../../core/logManager';

import { resetALL } from '../../../../../../../.globals/redux/actions';

import moment from "moment";

const DEFAULT_SORT = { key: 'nextMaintenanceStatus', order: 'asc' };

//STATE INITIAL
const INIT_STATE = {
    loading: false,

    loadingVehicles: false,
    filters: {
        status: [],
        groups: [],
        tags: [],
        services: [],
        vehicles: [],
    },
    sort: DEFAULT_SORT,

    loadingServicesList: false,
    servicesList: [],

    loadingVehicles: false,
    vehicles: [],
    items: [],
    total: 0,

    error: null
};

const route = 'modules/maintenance/views/main/components/vehiclesTab/redux/slice'

export const maintenanceVTGetServicesList = createAsyncThunk('maintenance/vehiclesTab/maintenanceVTGetServicesList', async ({ }, { rejectWithValue, dispatch }) => {
    try {
        const response = await clientQuery(
            `
                query {
                    data: searchMaintenanceServices {
                        id
                        title
                    }
                }
            `,
            {
            },
            endpoints.GRAPHQL_GENERAL
        );

        return response?.data || [];
    } catch (exc) {
        ExceptionManager(exc, route, 'maintenanceVTGetServicesList');
        return rejectWithValue(exc)
    }
});

export const maintenanceVTGetVehiclesData = createAsyncThunk(
    'maintenance/vehiclesTab/maintenanceVTGetVehiclesData',
    async ({ conditions, sort, limit = 10, offset = 0 }, { rejectWithValue, dispatch }) => {
    try {
        const getUnitIdsByServiceTerms = async (serviceTerms = []) => {
            const normalizedServiceIds = [...new Set(
                (serviceTerms || [])
                    .map((term) => Number(term))
                    .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0)
            )];

            if (!normalizedServiceIds.length) return null;

            const responses = await Promise.all(
                normalizedServiceIds.map((serviceId) =>
                    clientQuery(
                        `
                        query ($serviceId: Int, $excludeCompleted: Boolean) {
                            data: searchMaintenanceServiceUnits(serviceId: $serviceId, excludeCompleted: $excludeCompleted) {
                                unitId
                            }
                        }
                        `,
                        {
                            serviceId,
                            excludeCompleted: true,
                        },
                        endpoints.GRAPHQL_GENERAL
                    )
                )
            );

            const unitIds = new Set();
            responses.forEach((response) => {
                const items = response?.data || [];
                items.forEach((item) => {
                    if (item?.unitId !== undefined && item?.unitId !== null) {
                        unitIds.add(`${item.unitId}`);
                    }
                });
            });

            return unitIds;
        };

        const executeQuery = async (conditionsToUse, limitToUse = limit, offsetToUse = offset) => {
            return await clientQuery(
                `
            query MaintenanceVehicles(
                $conditions: [MaintenanceUnitSearchInput!]
                $sort: [MaintenanceUnitSortInput!]
                $limit: Int
                $offset: Int
            ) {
                data: searchMaintenanceUnits(
                    conditions: $conditions
                    sort: $sort
                    limit: $limit
                    offset: $offset
                ) {
                    items {
                        id
                        label
                        jbus
                        vbus
                        useEcuEngineHours
                        useEcuOdometer
                        offsetEcuEngineOdometer
                        offsetEngineOdometer
                        offsetEcuEngineHours
                        offsetEngineHours

                        unitVersion {
                            odometerEnable
                            hoursEnable
                        }

                        nextMaintenanceServiceUnit {
                            id
                            status
                            serviceId
                        }

                        tags {
                            id
                            label
                        }

                        lastReading {
                            engineOdometer
                            ecuEngineOdometer
                            engineHours
                            ecuEngineHours
                        }

                        lastMaintenanceServiceUnitLog {
                            id
                            maintenanceDate
                            odometer
                            serviceUnit {
                                service {
                                    title
                                }
                            }
                        }

                        dtcCount {
                            history
                            active
                        }
                    }
                    total
                }
            }
            `,
                {
                    conditions: conditionsToUse,
                    sort,
                    limit: limitToUse,
                    offset: offsetToUse,
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
        };

        const incomingConditions = Array.isArray(conditions) ? [...conditions] : [];
        let conditionsForRequest = [...incomingConditions];
        let forcedStatusTermsForClientFilter = null;
        let forcedServiceTermsForClientFilter = null;
        let forcedServiceUnitIdsForClientFilter = null;

        const statusCondition = conditionsForRequest.find((condition) => {
            const fields = condition?.fields || [];
            return fields.length === 1 && fields[0] === 'nextMaintenanceStatus';
        });

        if (statusCondition) {
            const statusTerms = (statusCondition?.terms || []).map((term) => `${term}`);
            if (statusTerms.length > 0) {
                forcedStatusTermsForClientFilter = statusTerms;
                conditionsForRequest = conditionsForRequest.filter((condition) => condition !== statusCondition);
            }
        }

        const serviceCondition = conditionsForRequest.find((condition) => {
            const fields = condition?.fields || [];
            return fields.length === 1 && fields[0] === 'nextMaintenanceServiceUnit_serviceId';
        });

        if (serviceCondition) {
            const serviceTerms = (serviceCondition?.terms || []).map((term) => `${term}`);
            if (serviceTerms.length > 0) {
                forcedServiceTermsForClientFilter = serviceTerms;
                conditionsForRequest = conditionsForRequest.filter((condition) => condition !== serviceCondition);
                forcedServiceUnitIdsForClientFilter = await getUnitIdsByServiceTerms(serviceTerms);
            }
        }

        let response;
        try {
            if (forcedStatusTermsForClientFilter?.length || forcedServiceTermsForClientFilter?.length) {
                const responseWithoutComplexConditions = await executeQuery(conditionsForRequest, 5000, 0);
                const allItems = responseWithoutComplexConditions?.data?.items || [];
                const filteredItems = allItems.filter((item) => {
                    const itemStatus = item?.nextMaintenanceServiceUnit?.status;
                    const lastMainUnitLog = item?.lastMaintenanceServiceUnitLog;
                    if (forcedStatusTermsForClientFilter?.length) {
                        // Vehicles with no nextMaintenanceServiceUnit are "Done" (service completed, none pending)
                        const isDoneFilter = forcedStatusTermsForClientFilter.includes("Done");
                        const isNullStatusTreatedAsDone = !itemStatus && isDoneFilter && lastMainUnitLog;
                        if (!isNullStatusTreatedAsDone && !forcedStatusTermsForClientFilter.includes(`${itemStatus}`)) {
                            return false;
                        }
                    }

                    if (
                        forcedServiceTermsForClientFilter?.length &&
                        forcedServiceUnitIdsForClientFilter &&
                        !forcedServiceUnitIdsForClientFilter.has(`${item?.id}`)
                    ) {
                        return false;
                    }

                    return true;
                });
                const paginatedItems = filteredItems.slice(offset, offset + limit);

                response = {
                    data: {
                        items: paginatedItems,
                        total: filteredItems.length,
                    },
                };
            } else {
                response = await executeQuery(conditionsForRequest);
            }
        } catch (exc) {
            const errorText = exc?.error || exc?.message || exc?.innerError?.message || '';
            const hasInvalidFieldsError = `${errorText}`.toLowerCase().includes("variable 'fields' has an invalid value".toLowerCase());

            if (hasInvalidFieldsError && Array.isArray(conditionsForRequest)) {
                let fallbackConditions = [...conditionsForRequest];
                let vehicleTermsForClientFilter = null;
                let fallbackStatusTermsForClientFilter = null;
                let serviceTermsForClientFilter = null;

                const detachTermsForClientFilter = (targetField) => {
                    const condition = fallbackConditions.find((item) => {
                        const fields = item?.fields || [];
                        return fields.length === 1 && fields[0] === targetField;
                    });

                    if (!condition) return null;

                    const terms = (condition?.terms || []).map((term) => `${term}`);
                    fallbackConditions = fallbackConditions.filter((item) => item !== condition);
                    return terms;
                };

                fallbackStatusTermsForClientFilter = detachTermsForClientFilter('nextMaintenanceStatus');
                serviceTermsForClientFilter = detachTermsForClientFilter('nextMaintenanceServiceUnit_serviceId');
                vehicleTermsForClientFilter = detachTermsForClientFilter('id');

                const statusTermsForClientFilter = [
                    ...(forcedStatusTermsForClientFilter || []),
                    ...(fallbackStatusTermsForClientFilter || []),
                ];
                const mergedServiceTermsForClientFilter = [
                    ...(forcedServiceTermsForClientFilter || []),
                    ...(serviceTermsForClientFilter || []),
                ];
                const serviceUnitIdsForClientFilter = mergedServiceTermsForClientFilter.length
                    ? (forcedServiceUnitIdsForClientFilter || await getUnitIdsByServiceTerms(mergedServiceTermsForClientFilter))
                    : null;

                const withoutUnsupportedServiceField = fallbackConditions.filter((condition) => {
                    const fields = condition?.fields || [];
                    return !fields.includes('nextMaintenanceServiceUnit_serviceId');
                });

                if (withoutUnsupportedServiceField.length !== fallbackConditions.length) {
                    fallbackConditions = withoutUnsupportedServiceField;
                }

                const changedFallback =
                    fallbackConditions.length !== conditionsForRequest.length ||
                    Boolean(vehicleTermsForClientFilter) ||
                    Boolean(statusTermsForClientFilter.length) ||
                    Boolean(mergedServiceTermsForClientFilter.length);

                if (!changedFallback) {
                    throw exc;
                }

                if (
                    vehicleTermsForClientFilter?.length ||
                    statusTermsForClientFilter?.length ||
                    mergedServiceTermsForClientFilter?.length
                ) {
                    const responseWithoutComplexConditions = await executeQuery(fallbackConditions, 5000, 0);
                    const allItems = responseWithoutComplexConditions?.data?.items || [];
                    const filteredItems = allItems.filter((item) => {
                        if (vehicleTermsForClientFilter?.length && !vehicleTermsForClientFilter.includes(`${item?.id}`)) {
                            return false;
                        }

                        const itemStatus = item?.nextMaintenanceServiceUnit?.status;
                        const lastMainUnitLog = item?.lastMaintenanceServiceUnitLog;
                        if (statusTermsForClientFilter?.length) {
                            const isDoneFilter = statusTermsForClientFilter.includes("Done");
                            const isNullStatusTreatedAsDone = !itemStatus && isDoneFilter && lastMainUnitLog;
                            if (!isNullStatusTreatedAsDone && !statusTermsForClientFilter.includes(`${itemStatus}`)) {
                                return false;
                            }
                        }

                        if (
                            mergedServiceTermsForClientFilter?.length &&
                            serviceUnitIdsForClientFilter &&
                            !serviceUnitIdsForClientFilter.has(`${item?.id}`)
                        ) {
                            return false;
                        }

                        return true;
                    });
                    const paginatedItems = filteredItems.slice(offset, offset + limit);

                    response = {
                        data: {
                            items: paginatedItems,
                            total: filteredItems.length,
                        },
                    };
                } else {
                    response = await executeQuery(fallbackConditions);
                }
            } else {
                throw exc;
            }
        }

        return response?.data || { items: [], total: 0 };
    } catch (exc) {
        ExceptionManager(exc, route, 'maintenanceVTGetVehiclesData');
        return rejectWithValue(exc)
    }
});


//Slice
const maintenanceVehiclesTabRedux = createSlice({
    name: 'maintenance/vehiclesTab',
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE
        },
        changeFilters(state, action) {
            state.filters = action.payload
        },
        changeSort(state, action) {
            state.sort = action.payload
        },
        changeVehicles(state, action) {
            state.vehicles = action.payload.vehicles
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //maintenanceVTGetServicesList
        builder.addCase(maintenanceVTGetServicesList.pending, (state, action) => {
            state.loadingServicesList = true
        });
        builder.addCase(maintenanceVTGetServicesList.fulfilled, (state, action) => {
            state.loadingServicesList = false;
            state.servicesList = action.payload
        });
        builder.addCase(maintenanceVTGetServicesList.rejected, (state, action) => {
            state.loadingServicesList = false;
            state.error = action.payload
        });

        //maintenanceVTGetVehiclesData
        builder.addCase(maintenanceVTGetVehiclesData.pending, (state, action) => {
            state.loadingVehicles = true
        });
        builder.addCase(maintenanceVTGetVehiclesData.fulfilled, (state, action) => {
            state.loadingVehicles = false;

            const rawItems = action.payload?.items || [];

            const vehicles = rawItems.map(item => ({
                ...item,
                nextMaintenanceStatus: item?.nextMaintenanceServiceUnit?.status,
                lastServiceTitle: item?.lastMaintenanceServiceUnitLog?.serviceUnit?.service?.title || '',
                engineHoursTotal: item?.lastReading?.ecuEngineHours ?? item?.lastReading?.engineHours ?? 0,
            }));

            state.items = vehicles;
            state.vehicles = vehicles;
            state.total = action.payload?.total || vehicles.length || 0;
        });
        builder.addCase(maintenanceVTGetVehiclesData.rejected, (state, action) => {
            state.loadingVehicles = false;
            state.error = action.payload
        });
    },
})

// Extract and export the action creators object and the reducer
export const { actions, reducer } = maintenanceVehiclesTabRedux;

// Extract and export each action creator by name
export const { reset, changeFilters, changeSort, changeVehicles } = actions;

//Simple async action

// Export the reducer, either as a default or named export
export default reducer;